import type { ReactElement } from 'react';

/** A heading entry for the table-of-contents sidebar. */
export interface TocEntry {
  /** Slug id (matches the rendered heading's `id`, for anchor scroll). */
  readonly id: string;
  /** Visible heading text. */
  readonly text: string;
  /** Heading level 1–6. */
  readonly depth: number;
}

/** A single find-in-document match within the document's plain text. */
export interface FindMatch {
  /** Character offset into the plain text. */
  readonly index: number;
  /** Match length. */
  readonly length: number;
}

/** Options controlling a single render. */
export interface RenderOptions {
  /**
   * Resolve an image `src` to a loadable URL (e.g. relative → asset: URL on
   * desktop). Identity by default. Return `null` to signal the asset is
   * inaccessible — the renderer then omits `src` entirely so the browser shows
   * the alt text instead of requesting a bogus URL. Keeps the renderer
   * platform-agnostic — the caller injects platform behavior.
   */
  readonly transformImageUrl?: (src: string) => string | null;
}

/** The result of rendering a markdown document. */
export interface RenderResult {
  /** The rendered React tree. */
  readonly element: ReactElement;
  /** Headings in document order (flat; depth carries the level). */
  readonly toc: TocEntry[];
  /** Concatenated visible text, for find-in-document match counting. */
  readonly plainText: string;
}

/** CSS class placed on mermaid diagram containers; the UI runs mermaid on these. */
export const MERMAID_CLASS = 'markroom-mermaid';
